import React, { useState, useEffect, useRef, useCallback } from "react";
import { Table } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import CandidateTableBody from "./CandidateTableBody";
import { renderCustomPagination } from "./candidateHelpers";


const AllCandidatesTab = ({
    candidates,
    loading,
    onViewSummary,
    onDownload,
    onAddClick,
    // filter props
    folderOptions,
    folderLoading,
    listOptions,
    listLoading,
    onFilterChange,
    pagination,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchText, setSearchText] = useState("");
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedList, setSelectedList] = useState(null);
    const debounceRef = useRef(null);

    // Clear selected list when folder changes (list belongs to a folder)
    useEffect(() => {
        setSelectedList(null);
    }, [selectedFolder]);

    const triggerFilter = useCallback(
        (search, folder_id, list_id, limit = pageSize, page = 1) => {
            if (onFilterChange) {
                onFilterChange({ search, folder_id, list_id, limit, page });
            }
        },
        [onFilterChange, pageSize]
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            triggerFilter(value, selectedFolder?.value, selectedList?.value, pageSize, 1);
        }, 500);
    };

    const handleFolderChange = (option) => {
        setSelectedFolder(option);
        setSelectedList(null); // reset list when folder changes
        triggerFilter(searchText, option?.value, null, pageSize, 1);
    };

    const handleListChange = (option) => {
        setSelectedList(option);
        triggerFilter(searchText, selectedFolder?.value, option?.value, pageSize, 1);
    };

    const total = pagination?.total || candidates.length;
    const totalPages = Math.ceil(total / pageSize);
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, total);

    const selectStyles = {
        control: (base) => ({ ...base, minWidth: 167, borderRadius: "4px", color: "#1f2a37", fontSize: "14px", lineHeight: "18px", fontFamily: '"Inter", sans-serif' }),
        menu: (base) => ({ ...base, minWidth: 167, fontFamily: '"Inter", sans-serif' }),
        menuList: (base) => ({ ...base, overflowX: "hidden" }),
        placeholder: (base) => ({ ...base, color: "#1f2a37", fontFamily: '"Inter", sans-serif' }),
        option: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
        singleValue: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
        input: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
    };

    return (
        <div className="table-content">
            <div className="controls">
                {/* Search */}
                <div className="has-search">
                    <span className="form-control-feedback">
                        <img src="/images/search.svg" alt="" />
                    </span>
                    <input
                        type="text"
                        className="search-bar"
                        placeholder="Search"
                        value={searchText}
                        onChange={handleSearchChange}
                    />
                </div>

                <div className="filter-buttons">
                    {/* Folder filter */}
                    <CreatableSelect
                        inputId="all-candidates-folder-filter"
                        value={selectedFolder}
                        onChange={handleFolderChange}
                        options={folderOptions || []}
                        isLoading={folderLoading}
                        isDisabled={folderLoading}
                        isClearable
                        placeholder="All Folders"
                        maxMenuHeight={200}
                        classNamePrefix="rs-filter"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                    />

                    {/* List filter */}
                    <CreatableSelect
                        inputId="all-candidates-list-filter"
                        value={selectedList}
                        onChange={handleListChange}
                        options={(listOptions || []).map((l) => ({ value: l.id, label: l.name }))}
                        isLoading={listLoading}
                        isDisabled={listLoading}
                        isClearable
                        placeholder="All Lists"
                        maxMenuHeight={200}
                        classNamePrefix="rs-filter"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                    />
                </div>
            </div>

            <Table responsive className="job-table">
                <thead>
                    <tr>
                        <th style={{ paddingLeft: '30px' }}>Name</th>
                        <th className="text-center">Email</th>
                        <th className="text-center">Designation</th>
                        <th className="text-center">Added On</th>
                        <th>Skills</th>
                        <th className="text-center">List Name</th>
                        <th className="text-center">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan="6">
                                <div className="loading-overlay" style={{ alignItems: 'start', color: '#F75891', height: 'auto' }}>
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading candidates...</span>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    ) : (
                        <CandidateTableBody
                            candidates={candidates}
                            onViewSummary={onViewSummary}
                            onDownload={onDownload}
                            onAddClick={onAddClick}
                        />
                    )}
                </tbody>
            </Table>

            {!loading && total > 0 && (
                <div className="pagination d-flex flex-wrap align-items-center justify-content-between">
                    <div className="d-flex flex-wrap" style={{ gap: "40px" }}>
                        <div className="d-flex flex-wrap" style={{ gap: "20px" }}>
                            <div className="pagination-info d-flex align-items-center">
                                <span style={{ color: "#525B66" }}>
                                    Showing {start}–{end} of <strong>{total}</strong> Candidates
                                </span>
                            </div>
                            <div className="pagination-buttons">
                                {renderCustomPagination(totalPages, currentPage, (p) => {
                                    setCurrentPage(p);
                                    triggerFilter(searchText, selectedFolder?.value, selectedList?.value, pageSize, p);
                                })}
                            </div>
                        </div>
                        <div className="d-flex align-items-center">
                            <span style={{ color: '#525B66', lineHeight: '100%' }}>Candidates per page</span>&nbsp;&nbsp;&nbsp;&nbsp;
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    const newSize = parseInt(e.target.value);
                                    setPageSize(newSize);
                                    setCurrentPage(1);
                                    triggerFilter(searchText, selectedFolder?.value, selectedList?.value, newSize, 1);
                                }}
                                style={{ width: "46px", height: '32px', borderRadius: '2px', border: '1px solid #8A9199', color: '#8A9199' }}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllCandidatesTab;
